import { ReactNode } from "react";
import { ReadOnlyBadge } from "~/components/ReadOnlyBadge";
import { cn } from "~/lib/utils";

export interface TabHeaderProps {
  title?: string;
  count?: number;
  // Omit while showing a read-only view (matching Capabilities' Toolkits/
  // Connectors counters) to render "{count} {countLabel}" instead of
  // "{count} of {max} {countLabel}" — a quota only means something to an editor.
  max?: number;
  // Word following the count, e.g. "used" or "enabled".
  countLabel?: string;
  context?: string;
  description?: ReactNode;
  action?: ReactNode;
  showReadOnlyBadge?: boolean;
  // "page" (default) is the full-size tab title. "section" is sized down for
  // a sub-section header within a tab that has more than one (e.g. a list
  // alongside another settings block).
  size?: "page" | "section";
}

export const TabHeader = ({
  title,
  count,
  max,
  countLabel,
  context,
  description,
  action,
  showReadOnlyBadge,
  size = "page",
}: TabHeaderProps) => {
  const showCounter = count !== undefined;
  const counter = showCounter && (
    <>
      {count}
      {max !== undefined && <> of {max}</>}
      {countLabel && <> {countLabel}</>}
    </>
  );
  const isSection = size === "section";
  const showMetaLine = !isSection && (showCounter || !!context);

  return (
    <div className={cn("flex flex-col", isSection ? "gap-1" : "gap-3")}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className={cn("flex gap-2", isSection ? "items-baseline" : "items-center")}>
            {title && (
              <h2 className={isSection ? "text-base font-medium" : "text-2xl font-semibold"}>
                {title}
              </h2>
            )}
            {isSection && counter && (
              <span className="text-xs text-muted-foreground">{counter}</span>
            )}
            {showReadOnlyBadge && <ReadOnlyBadge />}
          </div>
          {showMetaLine && (
            <p className="text-xs text-muted-foreground">
              {counter}
              {showCounter && context && <span className="mx-1.5">·</span>}
              {context}
            </p>
          )}
          {isSection && context && <p className="text-xs text-muted-foreground">{context}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
  );
};
